import { Box } from '@mui/material';
import { useState } from 'react';

import { formatSettingsDate } from './formatDate';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineDateCell } from '@/components/planning/cells';

/**
 * A date drawn as a field, opening Planning's own date editor (`InlineDateCell`
 * — the native input plus the shared calendar) so dates are picked the same
 * way everywhere. The field look mirrors the task panel's date field, which is
 * private to `TaskInsightPanel`.
 */
const DATE_FIELD_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1,
  py: 0.5,
  mx: 0,
  border: 'var(--border-width-thin) solid var(--border-divider)',
  borderRadius: 'var(--border-radius-small)',
  cursor: 'pointer',
  '&:hover': { bgcolor: 'transparent', borderColor: 'var(--border-subtle)' },
} as const;

/**
 * The 40px form field Project details' forms use (docs/briefs/
 * project-details-v2 §3: "label above field, 40px fields") — the same
 * component and calendar, a taller box.
 */
const FIELD_SX = {
  ...DATE_FIELD_SX,
  boxSizing: 'border-box',
  height: 'var(--input-height-medium)',
  px: 1.5,
  gap: 1,
  border: 'var(--border-width-thin) solid var(--border-subtle)',
  borderRadius: 'var(--border-radius-medium)',
  '&:hover': { bgcolor: 'transparent', borderColor: 'var(--color-fog-100)' },
} as const;

export type SettingsDateFieldProps = {
  /** ISO `YYYY-MM-DD`, or '' when not set. */
  value: string;
  placeholder: string;
  onCommit: (iso: string) => void;
  automationId: string;
  /** `field`: the 40px form field; `compact` (default): v1's inline field. */
  size?: 'compact' | 'field';
};

export const SettingsDateField = ({
  value,
  placeholder,
  onCommit,
  automationId,
  size = 'compact',
}: SettingsDateFieldProps) => {
  const [editing, setEditing] = useState(false);
  return (
    <InlineDateCell
      value={value}
      display={
        <>
          <MaterialSymbol name="calendar_today" size={14} color="var(--fg-3)" />
          {value ? (
            formatSettingsDate(value)
          ) : (
            <Box component="span" sx={{ color: 'var(--fg-3)' }}>
              {placeholder}
            </Box>
          )}
        </>
      }
      displaySx={size === 'field' ? FIELD_SX : DATE_FIELD_SX}
      editing={editing}
      autoOpen
      onStartEdit={() => setEditing(true)}
      onCommit={(iso) => {
        setEditing(false);
        if (iso !== value) onCommit(iso);
      }}
      onCancel={() => setEditing(false)}
      align="left"
      inputWidth={128}
      automationId={automationId}
    />
  );
};
